import { TrendingUp, TrendingDown } from "lucide-react";

/**
 * MobileDashboardCards — reference card layout for Dashboard tabs on mobile.
 * Reused across Exited Clients, Transfer Out, Transmission, Multi Broker,
 * Reduced Portfolio, Underperformers, Outperformers, ELSS Pending, Profit
 * Realignment, Unpaid Brokerage, Historical Data.
 *
 * Each card stacks the row's most important metrics so users scroll
 * vertically only (no horizontal scroll table).
 *
 * Props:
 *   rows       : Array of row objects (client / folio / txn)
 *   config     : {
 *     title       : (row) => string           // main headline
 *     subtitle?   : (row) => string           // small line under title
 *     chips?      : (row) => Array<{ label, color? }>  // top-right chips
 *     stats       : Array<{ label, value: (row) => any, tone?: (row) => 'positive'|'negative'|'default' }>
 *     footer?     : (row) => Array<{ label, className? }>  // bottom chips
 *     onClick?    : (row) => void             // makes card clickable
 *     testIdPrefix: string
 *   }
 *   emptyMsg?  : string
 */
const fmtINR = (v) => (parseFloat(v) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtCompact = (v) => {
  const n = parseFloat(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${fmtINR(n)}`;
};

const toneClass = (tone) => {
  if (tone === "positive") return "text-emerald-700";
  if (tone === "negative") return "text-red-700";
  return "text-gray-800";
};

const MobileDashboardCard = ({ row, config, index }) => {
  const clickable = typeof config.onClick === "function";
  return (
    <div
      role={clickable ? "button" : undefined}
      onClick={clickable ? () => config.onClick(row) : undefined}
      className={`bg-white border border-gray-200 rounded-xl p-3 shadow-sm ${clickable ? "cursor-pointer active:scale-[0.99] active:bg-gray-50" : ""} transition-transform`}
      data-testid={`${config.testIdPrefix}-mobile-${index}`}
    >
      {/* Top row: title/subtitle + chips */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2">
            {config.title(row) || "—"}
          </p>
          {config.subtitle && (
            <p className="text-[11px] text-gray-500 mt-0.5">{config.subtitle(row)}</p>
          )}
        </div>
        {config.chips && (
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            {config.chips(row).map((chip, i) => (
              <span
                key={i}
                className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${chip.color || "bg-gray-100 text-gray-700"}`}
              >
                {chip.label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Stats grid */}
      {config.stats && config.stats.length > 0 && (
        <div className={`grid gap-2 pt-2 border-t border-gray-100 ${config.stats.length === 2 ? "grid-cols-2" : config.stats.length === 4 ? "grid-cols-2" : "grid-cols-3"}`}>
          {config.stats.map((stat, i) => {
            const val = stat.value(row);
            const tone = stat.tone ? stat.tone(row) : "default";
            return (
              <div key={i}>
                <p className="text-[10px] uppercase tracking-wider text-gray-500">{stat.label}</p>
                <p className={`text-xs font-semibold mt-0.5 ${toneClass(tone)}`}>{val}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer chips */}
      {config.footer && (
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {config.footer(row).map((f, i) => (
            <span
              key={i}
              className={`text-[10px] px-1.5 py-0.5 rounded ${f.className || "bg-gray-100 text-gray-600"}`}
            >
              {f.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const MobileDashboardCards = ({ rows = [], config, emptyMsg = "No records found" }) => {
  if (!rows.length) {
    return (
      <div className="py-8 text-center text-sm text-gray-400" data-testid={`${config.testIdPrefix}-mobile-empty`}>
        {emptyMsg}
      </div>
    );
  }
  return (
    <div className="space-y-2.5" data-testid={`${config.testIdPrefix}-mobile-list`}>
      {rows.map((row, index) => (
        <MobileDashboardCard
          key={`${config.testIdPrefix}-${row.pan || row.folio_no || index}-${index}`}
          row={row}
          config={config}
          index={index}
        />
      ))}
    </div>
  );
};

export { fmtCompact, fmtINR, TrendingUp, TrendingDown };
export default MobileDashboardCards;
